import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  X, ChevronRight, ChevronLeft, SkipForward, 
  LayoutDashboard, Bell, MapPin, Wrench, Users, 
  Calendar, Car, ClipboardList, FileText, Calculator,
  CheckCircle2, Sparkles, Target, Lightbulb
} from "lucide-react";

const TUTORIAL_STEPS = [
  {
    id: "welcome",
    title: "Bine ai venit în e-Tesa! 🎉",
    description: "Acest tutorial te va ghida prin funcționalitățile principale ale aplicației. Durează aproximativ 2 minute.",
    icon: Sparkles,
    position: "center",
    highlight: null
  },
  {
    id: "dashboard",
    title: "Dashboard - Acasă",
    description: "Aici vezi prezențele de astăzi ale angajaților, statistici rapide și poți genera pontaj automat pentru echipă.",
    icon: LayoutDashboard,
    position: "bottom",
    highlight: '[data-testid="dashboard-tab"]',
    tip: "💡 Click pe cardul unui angajat pentru a edita pontajul său."
  },
  {
    id: "notifications",
    title: "Notificări",
    description: "Primești alerte pentru cereri de concediu, pauze și alte evenimente importante. Badge-ul roșu arată notificările necitite.",
    icon: Bell,
    position: "bottom",
    highlight: '[data-testid="notifications-tab"]',
    tip: "💡 Poți șterge notificările în bloc selectându-le."
  },
  {
    id: "sites",
    title: "Șantiere",
    description: "Adaugă și gestionează locațiile de lucru. Fiecare șantier poate avea coordonate GPS pentru tracking.",
    icon: MapPin,
    position: "bottom",
    highlight: '[data-testid="sites-tab"]',
    tip: "💡 Angajații vor selecta șantierul la check-in."
  },
  {
    id: "tools",
    title: "Unelte",
    description: "Inventarul de unelte și echipamente. Poți urmări utilizarea și disponibilitatea fiecărei unelte.",
    icon: Wrench,
    position: "bottom",
    highlight: '[data-testid="tools-tab"]',
    tip: "💡 Sistemul previne rezervarea dublă a uneltelor."
  },
  {
    id: "workers",
    title: "Angajați",
    description: "Adaugă angajați noi, editează datele lor și vezi istoricul de pontaj pentru fiecare.",
    icon: Users,
    position: "bottom",
    highlight: '[data-testid="workers-tab"]',
    tip: "💡 Angajații primesc automat acces la aplicația mobilă."
  },
  {
    id: "pontaj",
    title: "Calendar Pontaj",
    description: "Vizualizează și editează pontajul lunar pentru toți angajații. Poți seta concedii, absențe și ore suplimentare.",
    icon: Calendar,
    position: "bottom",
    highlight: '[data-testid="leaves-tab"]',
    tip: "💡 Click pe orice celulă din calendar pentru editare rapidă."
  },
  {
    id: "trips",
    title: "Foi de Parcurs",
    description: "Gestionează deplasările cu mașina firmei. Angajații completează km la plecare și sosire.",
    icon: Car,
    position: "bottom",
    highlight: '[data-testid="trips-tab"]',
    tip: "💡 Foile de parcurs se înregistrează automat în documente."
  },
  {
    id: "tasks",
    title: "Sarcini",
    description: "Creează și atribuie sarcini angajaților. Urmărește progresul și adaugă fotografii.",
    icon: ClipboardList,
    position: "bottom",
    highlight: '[data-testid="tasks-tab"]',
    tip: "💡 Setează priorități pentru organizare mai bună."
  },
  {
    id: "documents",
    title: "Documente",
    description: "Registrul automat de documente: cereri concediu aprobate, foi de parcurs completate.",
    icon: FileText,
    position: "bottom",
    highlight: '[data-testid="documents-tab"]',
    tip: "💡 Fiecare document primește număr unic automat."
  },
  {
    id: "deviz",
    title: "Deviz (Formular F3)",
    description: "Generează devize de lucrări pentru proiectele tale de construcții.",
    icon: Calculator,
    position: "bottom",
    highlight: '[data-testid="deviz-tab"]',
    tip: "💡 Exportă devizele în format PDF."
  },
  {
    id: "complete",
    title: "Tutorial Complet! ✅",
    description: "Acum cunoști toate funcționalitățile e-Tesa. Poți accesa acest tutorial oricând din Setări.",
    icon: CheckCircle2,
    position: "center",
    highlight: null
  }
];

export default function TutorialOverlay({ isOpen, onClose, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);

  const step = TUTORIAL_STEPS[currentStep];
  const progress = ((currentStep + 1) / TUTORIAL_STEPS.length) * 100;

  useEffect(() => {
    if (!isOpen) return;

    const updateHighlight = () => {
      if (step.highlight) {
        const element = document.querySelector(step.highlight);
        if (element) {
          const rect = element.getBoundingClientRect();
          setHighlightRect({
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16
          });
          
          // Scroll element into view if needed
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        setHighlightRect(null);
      }
    };

    updateHighlight();
    window.addEventListener('resize', updateHighlight);
    
    return () => window.removeEventListener('resize', updateHighlight);
  }, [currentStep, step.highlight, isOpen]);

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const handleDontShowAgain = () => {
    // Salvează timestamp-ul + 2 săptămâni
    const twoWeeksFromNow = Date.now() + (14 * 24 * 60 * 60 * 1000);
    localStorage.setItem('etesa_tutorial_hidden_until', twoWeeksFromNow.toString());
    localStorage.setItem('etesa_tutorial_completed', 'true');
    onClose();
  };

  const handleComplete = () => {
    localStorage.setItem('etesa_tutorial_completed', 'true');
    onComplete && onComplete();
    onClose();
  };

  if (!isOpen) return null;

  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="tutorial-overlay">
      {/* Dark overlay - foarte transparent */}
      <div className="absolute inset-0 bg-black/30" />
      
      {/* Highlight cutout - complet transparent */}
      {highlightRect && (
        <div
          className="absolute border-4 border-teal-400 rounded-xl transition-all duration-300"
          style={{
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
            boxShadow: '0 0 0 4px rgba(45, 212, 191, 0.3), 0 0 20px rgba(45, 212, 191, 0.4)'
          }}
        />
      )}

      {/* Tutorial Card */}
      <div 
        className={`absolute bg-white rounded-2xl shadow-2xl p-6 w-[90%] max-w-md transition-all duration-300 ${
          step.position === 'center' 
            ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
            : highlightRect
              ? 'top-auto left-1/2 -translate-x-1/2'
              : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
        }`}
        style={
          step.position === 'bottom' && highlightRect
            ? { top: highlightRect.top + highlightRect.height + 20 }
            : {}
        }
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-slate-200 rounded-t-2xl overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-teal-500 to-blue-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-600 transition-colors"
          data-testid="tutorial-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Step counter */}
        <Badge className="bg-teal-100 text-teal-700 mb-4">
          Pasul {currentStep + 1} din {TUTORIAL_STEPS.length}
        </Badge>

        {/* Icon */}
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-blue-500 flex items-center justify-center mb-4 shadow-lg">
          <Icon className="w-8 h-8 text-white" />
        </div>

        {/* Content */}
        <h3 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {step.title}
        </h3>
        <p className="text-slate-600 mb-4 leading-relaxed">
          {step.description}
        </p>

        {/* Tip box */}
        {step.tip && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">{step.tip}</p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex flex-col gap-3 mt-6">
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-slate-500"
              data-testid="tutorial-skip"
            >
              <SkipForward className="w-4 h-4 mr-1" />
              Sari tutorial
            </Button>
            
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  data-testid="tutorial-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Înapoi
                </Button>
              )}
              <Button
                onClick={handleNext}
                className="bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600"
                data-testid="tutorial-next"
              >
                {currentStep === TUTORIAL_STEPS.length - 1 ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Finalizează
                  </>
                ) : (
                  <>
                    Continuă
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {/* Don't show again checkbox */}
          <button
            onClick={handleDontShowAgain}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors flex items-center justify-center gap-1.5 py-1"
            data-testid="tutorial-dont-show"
          >
            <div className="w-3.5 h-3.5 border border-slate-300 rounded flex items-center justify-center">
              <X className="w-2.5 h-2.5 opacity-0 hover:opacity-100" />
            </div>
            Nu mai arăta tutorialul (2 săptămâni)
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-1.5 mt-4">
          {TUTORIAL_STEPS.map((step, idx) => (
            <button
              key={step.title || `step-${idx}`}
              onClick={() => setCurrentStep(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentStep 
                  ? 'bg-teal-500 w-6' 
                  : idx < currentStep 
                    ? 'bg-teal-300' 
                    : 'bg-slate-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
